import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DEFECT_TYPES, DEFECT_STATUS, SEVERITY, formatDate } from "@/lib/dict";
import { Plus, Home as HomeIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function TenantPortal() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ title: "", description: "", type: "plumbing", severity: "minor", asset_id: "asset-001" });

  const { data: defects = [] } = useQuery({ queryKey: ["tenant-defects"], queryFn: () => api.get("/tenant/defects").then((r) => r.data) });

  const create = useMutation({
    mutationFn: (d) => api.post("/defects", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tenant-defects"] }); toast.success("Déclaration envoyée"); setOpen(false); setStep(1); setForm({ title: "", description: "", type: "plumbing", severity: "minor", asset_id: "asset-001" }); },
  });

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#023E73", color: "white" }}><HomeIcon size={22} /></div>
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Portail locataire</h1>
          <p className="text-sm text-slate-500">Bonjour {user?.first_name}, suivez vos déclarations.</p>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setStep(1); }}>
        <DialogTrigger asChild>
          <Button data-testid="declare-btn" className="w-full h-14 text-white text-base font-semibold" style={{ background: "#023E73" }}>
            <Plus size={18} className="mr-2" /> Déclarer un problème
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nouvelle déclaration • Étape {step}/3</DialogTitle></DialogHeader>
          {step === 1 && (
            <div className="space-y-3">
              <div><Label>Titre court</Label><Input data-testid="tenant-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Fuite sous l'évier" /></div>
              <div><Label>Type de problème</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="tenant-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(DEFECT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3">
              <div><Label>Description détaillée</Label><Textarea data-testid="tenant-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} placeholder="Décrivez le problème..." /></div>
              <div><Label>Urgence</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="tenant-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SEVERITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="font-semibold">{form.title}</div>
                <div className="text-xs text-slate-600 mt-1">{DEFECT_TYPES[form.type]} • {SEVERITY[form.severity].label}</div>
                <div className="text-sm text-slate-700 mt-2">{form.description}</div>
              </div>
              <p className="text-xs text-slate-500">En validant, vous confirmez l'envoi de cette déclaration au gestionnaire.</p>
            </div>
          )}
          <DialogFooter>
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)}>Précédent</Button>}
            {step < 3 && <Button data-testid="tenant-next" onClick={() => setStep(step + 1)} disabled={step === 1 && !form.title} className="text-white" style={{ background: "#023E73" }}>Suivant <ArrowRight size={14} className="ml-1" /></Button>}
            {step === 3 && <Button data-testid="tenant-submit" onClick={() => create.mutate(form)} className="text-white" style={{ background: "#023E73" }}>Envoyer la déclaration</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div>
        <h2 className="font-display text-lg font-semibold mb-3">Mes demandes ({defects.length})</h2>
        <div className="space-y-3">
          {defects.map((d) => (
            <Card key={d.id} data-testid={`tenant-defect-${d.id}`} className="p-4 border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-mono text-[10px] text-slate-400">{d.reference}</div>
                  <div className="font-semibold text-slate-900 mt-0.5">{d.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{DEFECT_TYPES[d.type]} • {formatDate(d.date_observed || d.created_at)}</div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${DEFECT_STATUS[d.status]?.color}`}>{DEFECT_STATUS[d.status]?.label}</span>
                </div>
              </div>
            </Card>
          ))}
          {defects.length === 0 && <div className="text-sm text-slate-400 text-center py-8">Aucune déclaration pour le moment</div>}
        </div>
      </div>
    </div>
  );
}
